import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, DatePicker, message, Divider, Upload, Image, Space } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined, DeleteOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../store';
import { setAuth, updateUser } from '../store/slices/authSlice';
import { setFiscalYearEndDate, setMaterialityDescription } from '../store/slices/annualReportSlice';
import { getCurrentUser, updateProfile, changePassword } from '../services/authService';
import { updateAnnualReport, rolloverAnnualReport, saveMaterialityDescription } from '../services/financialStatementService';
import type { UpdateInternalUserProfileRequest, UpdateProcessOwnerProfileRequest } from '../services/types';
import ReportRolloverModal from './ReportRolloverModal';
import { getAnnualReports } from '../services/financialStatementService';
import { setReports } from '../store/slices/annualReportSlice';
import { uploadCompanyLogo, deleteCompanyLogo } from '../services/fileService';
//import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const LOGO_MAX_MB = 2;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
`;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 32px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 8px;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  .section-description {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 24px;

    @media (max-width: 768px) {
      font-size: 13px;
      margin-bottom: 16px;
    }
  }

  .ant-form-item {
    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
  }
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #F0F0F0;

  &:last-child {
    border-bottom: none;
  }

  .label {
    font-weight: 500;
    color: #374151;
    font-size: 14px;

    @media (max-width: 768px) {
      font-size: 13px;
    }
  }

  .value {
    color: #6B7280;
    font-size: 14px;
    text-align: right;

    @media (max-width: 768px) {
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    padding: 10px 0;
    flex-wrap: wrap;
    gap: 4px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const PasswordRequirementItem = styled.div<{ $passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$passed ? '#0C827A' : '#848787'};
  margin-bottom: 6px;

  .anticon {
    font-size: 14px;
    color: ${props => props.$passed ? '#0C827A' : '#848787'};
  }
`;

const AccountSettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);

  const [profileForm] = Form.useForm();
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingFiscalDate, setIsSavingFiscalDate] = useState(false);
  const [fiscalDate, setFiscalDate] = useState<dayjs.Dayjs | null>(null);

  const isInternalAdmin = user?.userType === 'internalAdministrators';
  const isProcessOwner = user?.userType === 'processOwners';

  const [isRolloverModalOpen, setIsRolloverModalOpen] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isLogoRemoving, setIsLogoRemoving] = useState(false);
  const [materialityDescriptionText, setMaterialityDescriptionText] = useState('');
  const [isSavingMaterialityDesc, setIsSavingMaterialityDesc] = useState(false);

  const [passwordForm] = Form.useForm();
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordFormValid, setIsPasswordFormValid] = useState(false);

  const pwHasMinLength = newPassword.length >= 16;
  const pwHasNumber = /\d/.test(newPassword);
  const pwHasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
  const pwHasUpperAndLower = /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword);
  //const navigate = useNavigate();

  // Populate form with user data
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        companyName: user.companyName || '',
        jobTitle: user.jobTitle || '',
        department: user.department || '',
      });
    }
  }, [user, profileForm]);

  // Set fiscal date from selected report
  useEffect(() => {
    if (selectedReport?.fiscalYearEndDate) {
      setFiscalDate(dayjs(selectedReport.fiscalYearEndDate));
    }
    setMaterialityDescriptionText(selectedReport?.materialityDescription ?? '');
  }, [selectedReport]);

  const logoUploadProps: UploadProps = {
    accept: LOGO_ACCEPT,
    maxCount: 1,
    showUploadList: false,
    disabled: isLogoUploading || !user?.companyId,
    beforeUpload: (file) => {
      const allowed = ['image/png', 'image/jpeg', 'image/webp'];
      if (!allowed.includes(file.type)) {
        message.error('Only PNG, JPEG, or WebP images are allowed.');
        return Upload.LIST_IGNORE;
      }
      if (file.size / 1024 / 1024 >= LOGO_MAX_MB) {
        message.error(`Image must be smaller than ${LOGO_MAX_MB}MB.`);
        return Upload.LIST_IGNORE;
      }
      void (async () => {
        if (!user?.companyId) return;
        setIsLogoUploading(true);
        try {
          await uploadCompanyLogo(user.companyId, file);
          const userResponse = await getCurrentUser();
          dispatch(setAuth({ user: userResponse.data }));
          message.success('Company logo updated');
        } catch (error: any) {
          message.error(error?.message || 'Failed to upload company logo');
        } finally {
          setIsLogoUploading(false);
        }
      })();
      return false;
    },
  };

  const handleRemoveCompanyLogo = async () => {
    if (!user?.companyId) return;
    setIsLogoRemoving(true);
    try {
      await deleteCompanyLogo(user.companyId);
      dispatch(updateUser({ companyImageUrl: null }));
      message.success('Company logo removed');
    } catch (error: any) {
      message.error(error?.message || 'Failed to remove company logo');
    } finally {
      setIsLogoRemoving(false);
    }
  };

  const handleProfileSave = async () => {
    try {
      const values = await profileForm.validateFields();
      setIsSavingProfile(true);

      let payload: UpdateInternalUserProfileRequest | UpdateProcessOwnerProfileRequest;

      if (isInternalAdmin) {
        payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          companyName: values.companyName,
        } as UpdateInternalUserProfileRequest;
      } else {
        payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          jobTitle: values.jobTitle,
          department: values.department,
        } as UpdateProcessOwnerProfileRequest;
      }

      const response = await updateProfile(payload);

      // Update Redux store with the returned user data
      if (response.data) {
        dispatch(updateUser(response.data));
      } else {
        // Fallback: update with the values we sent
        dispatch(updateUser(payload));
      }

      message.success('Profile updated successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update profile';
      message.error(errorMessage);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleMaterialityDescSave = async () => {
    if (!selectedReport?.financialStatementId) return;
    try {
      setIsSavingMaterialityDesc(true);
      const value = materialityDescriptionText.trim() || null;
      await saveMaterialityDescription(selectedReport.financialStatementId, value);
      dispatch(setMaterialityDescription(value));
      message.success('Materiality description updated successfully');
    } catch (error: any) {
      message.error(error?.message || 'Failed to update materiality description');
    } finally {
      setIsSavingMaterialityDesc(false);
    }
  };

  const handleFiscalDateSave = async () => {
    if (!fiscalDate || !selectedReport) return;

    try {
      setIsSavingFiscalDate(true);

      await updateAnnualReport({
        fiscalYearEndDate: fiscalDate.toISOString(),
        annualReportId: selectedReport.annualReportId,
      });

      // Update Redux store
      dispatch(setFiscalYearEndDate({
        annualReportId: selectedReport.annualReportId,
        fiscalYearEndDate: fiscalDate.toISOString(),
      }));

      message.success('Fiscal Year End Date updated successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update Fiscal Year End Date';
      message.error(errorMessage);
    } finally {
      setIsSavingFiscalDate(false);
    }
  };

  const handlePasswordFormChange = (_changedValues: any, allValues: any) => {
    const current = allValues.currentPassword?.trim() || '';
    const pwd = allValues.newPassword?.trim() || '';
    const confirm = allValues.confirmPassword?.trim() || '';
    setNewPassword(allValues.newPassword || '');
    const meetsRequirements =
      pwd.length >= 16 &&
      /\d/.test(pwd) &&
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) &&
      /[A-Z]/.test(pwd) && /[a-z]/.test(pwd);
    setIsPasswordFormValid(current.length > 0 && meetsRequirements && pwd === confirm);
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      setIsSavingPassword(true);

      await changePassword({
        currentPassword: values.currentPassword,
        password: values.newPassword,
        passwordConfirmation: values.confirmPassword,
      });

      message.success('Password changed successfully');
      passwordForm.resetFields();
      setNewPassword('');
      setIsPasswordFormValid(false);
    } catch (error: any) {
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];
        const toastMessages: string[] = [];
        const fieldMap: Record<string, string> = {
          currentPassword: 'currentPassword',
          password: 'newPassword',
          passwordConfirmation: 'confirmPassword',
        };
        for (const [apiField, formField] of Object.entries(fieldMap)) {
          if (error.data[apiField] && Array.isArray(error.data[apiField])) {
            fieldErrors.push({ name: formField, errors: error.data[apiField] });
            toastMessages.push(...error.data[apiField]);
          }
        }
        if (fieldErrors.length > 0) {
          passwordForm.setFields(fieldErrors);
          message.error(toastMessages.join('. '));
          return;
        }
      }
      message.error(error?.message || 'Failed to change password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return dayjs(dateStr).format('MM/DD/YYYY');
  };

  const processRollover = async () => {
    //let year = 0;
    try {
      setIsSavingProfile(true);

      //var response = 
      await rolloverAnnualReport(selectedReport?.annualReportId ?? "");

      //year = response.data.year;

      message.success('Project fiscal year rollover completed successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to rollover project fiscal year';
      message.error(errorMessage);
    } finally {
      setIsSavingProfile(false);
      setIsRolloverModalOpen(false);

      try {
        const annualReports = await getAnnualReports();
        dispatch(setReports(annualReports));
        // console.log('✅ Annual reports loaded successfully');
      } catch (error: any) {
        console.error('❌ Failed to load project fiscal year reports:', error);
        message.error(error?.message || 'Failed to load project fiscal year reports');
      }

      //setSelectedReportByYear(year)
      //navigate('/dashboard');
    }
  };

  return (
    <div>
      <PageHeader>
        <PageTitle level={2}>Settings</PageTitle>
      </PageHeader>

      {/* Profile Section */}
      <StyledCard style={{ marginBottom: 24 }}>
        <FormSection>
          <div className="section-title">Profile Information</div>
          <div className="section-description">
            Update your personal information.
          </div>

          <Form form={profileForm} layout="vertical">
            <FormRow>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: 'Last name is required' }]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </FormRow>

            <FormRow>
              <Form.Item
                label="Phone Number"
                name="phoneNumber"
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>

              {isInternalAdmin && (
                <Form.Item
                  label="Company Name"
                  name="companyName"
                >
                  <Input placeholder="Enter company name" />
                </Form.Item>
              )}

              {isProcessOwner && (
                <Form.Item
                  label="Job Title"
                  name="jobTitle"
                >
                  <Input placeholder="Enter job title" />
                </Form.Item>
              )}
            </FormRow>

            {isProcessOwner && (
              <FormRow>
                <Form.Item
                  label="Department"
                  name="department"
                >
                  <Input placeholder="Enter department" />
                </Form.Item>
              </FormRow>
            )}

            <Button
              type="primary"
              onClick={handleProfileSave}
              loading={isSavingProfile}
              style={{ marginTop: 8 }}
            >
              Save Changes
            </Button>
          </Form>

          {isInternalAdmin && (
            <>
              <Divider />
              <div className="section-title">Company Logo</div>
              <div className="section-description">
                Upload a logo for your company. It appears in the sidebar and on PDF exports. PNG, JPEG, or WebP, max {LOGO_MAX_MB}MB.
              </div>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {user?.companyImageUrl ? (
                  <div style={{ maxWidth: 240, height: 120, display: 'flex', alignItems: 'center' }}>
                    <Image
                      src={"data:image/png;base64," + user.companyImageUrl}
                      alt="Company logo"
                      style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <Text type="secondary">No logo uploaded.</Text>
                )}
                <Space wrap>
                  <Upload {...logoUploadProps}>
                    <Button icon={<UploadOutlined />} loading={isLogoUploading}>
                      {user?.companyImageUrl ? 'Replace logo' : 'Upload logo'}
                    </Button>
                  </Upload>
                  {user?.companyImageUrl && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={isLogoRemoving}
                      onClick={handleRemoveCompanyLogo}
                    >
                      Remove logo
                    </Button>
                  )}
                </Space>
              </Space>
            </>
          )}

          <Divider />

          {/* Read-only account info */}
          <div className="section-title">Account Information</div>
          <div className="section-description">
            These fields are managed by the system and cannot be changed here.
          </div>

          <InfoItem>
            <span className="label">Email Address</span>
            <span className="value">{user?.email || 'N/A'}</span>
          </InfoItem>

          <InfoItem>
            <span className="label">Username</span>
            <span className="value">{user?.userName || 'N/A'}</span>
          </InfoItem>

          <InfoItem>
            <span className="label">User Type</span>
            <span className="value">{user?.userType || 'N/A'}</span>
          </InfoItem>

          <InfoItem>
            <span className="label">Location</span>
            <span className="value">{user?.location || 'N/A'}</span>
          </InfoItem>

        </FormSection>
      </StyledCard>

      {/* Change Password Section */}
      <StyledCard style={{ marginBottom: 24 }}>
        <FormSection>
          <div className="section-title">Change Password</div>
          <div className="section-description">
            Update your password. You will need to enter your current password for verification.
          </div>

          <Form
            form={passwordForm}
            layout="vertical"
            onValuesChange={handlePasswordFormChange}
            autoComplete="off"
            requiredMark={false}
          >
            <Form.Item
              label="Current Password"
              name="currentPassword"
              rules={[{ required: true, message: 'Current password is required' }]}
            >
              <Input.Password placeholder="Enter current password" autoComplete="current-password" />
            </Form.Item>

            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[{ required: true, message: 'New password is required' }]}
            >
              <Input.Password
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" autoComplete="new-password" />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Password must contain the following:
              </Text>
              <PasswordRequirementItem $passed={pwHasMinLength}>
                <CheckCircleFilled />
                <span>At least 16 characters</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasNumber}>
                <CheckCircleFilled />
                <span>At least one number</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasSpecialChar}>
                <CheckCircleFilled />
                <span>At least one special character</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasUpperAndLower}>
                <CheckCircleFilled />
                <span>At least one uppercase and lowercase character</span>
              </PasswordRequirementItem>
            </div>

            <Button
              type="primary"
              onClick={handlePasswordChange}
              loading={isSavingPassword}
              disabled={!isPasswordFormValid}
            >
              Change Password
            </Button>
          </Form>
        </FormSection>
      </StyledCard>

      {/* Materiality Preferences Section - Admin only */}
      {isInternalAdmin && (
        <StyledCard style={{ marginBottom: 24 }}>
          <FormSection>
            <div className="section-title">Materiality Preferences</div>
            <div className="section-description">
              Customize the materiality tooltip text displayed in Exhibit 2. Leave empty to use the default wording.
            </div>

            <Form layout="vertical">
              <Form.Item label="Custom Materiality Tooltip Text">
                <Input.TextArea
                  rows={3}
                  placeholder="e.g. 0.5% of Total Assets"
                  value={materialityDescriptionText}
                  onChange={(e) => setMaterialityDescriptionText(e.target.value)}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
              <Button
                type="primary"
                onClick={handleMaterialityDescSave}
                loading={isSavingMaterialityDesc}
              >
                Save
              </Button>
            </Form>
          </FormSection>
        </StyledCard>
      )}

      {/* Fiscal Year End Date Section */}
      <StyledCard>
        <FormSection>
          <div className="section-title">Fiscal Year End Date</div>
          <div className="section-description">
            {isInternalAdmin
              ? 'Set the fiscal year end date for the selected annual report.'
              : 'The fiscal year end date for the selected annual report.'}
          </div>

          {isInternalAdmin ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <DatePicker
                value={fiscalDate}
                onChange={(date) => setFiscalDate(date)}
                format="MM/DD/YYYY"
                style={{ width: 240 }}
                allowClear={false}
              />
              <Button
                type="primary"
                onClick={handleFiscalDateSave}
                loading={isSavingFiscalDate}
                disabled={!fiscalDate}
              >
                Save
              </Button>
            </div>
          ) : (
            <InfoItem>
              <span className="label">Fiscal Year End Date</span>
              <Text>{formatDate(selectedReport?.fiscalYearEndDate)}</Text>
            </InfoItem>
          )}
          {isInternalAdmin ? 
          (<Button
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                setIsRolloverModalOpen(true);
              }}
              style={{ marginTop: 40 }}
              loading={isSavingProfile}
            >
              Rollover Fiscal Year
            </Button>) : ""}
        </FormSection>
      </StyledCard>

      {/* Rollover Modal */}
          {isInternalAdmin ? (<ReportRolloverModal
            open={isRolloverModalOpen}
            onClose={() => {
              setIsRolloverModalOpen(false);
            }}
            onSubmit={processRollover}
          />) : ""}
    </div>

    
  );
};

export default AccountSettingsPage;
